import { FORBIDDEN, UNAUTHORIZED } from '@/constants/HttpStatus';
import { Middleware, MiddlewareAPI, PayloadAction, isRejected } from '@reduxjs/toolkit';
import { extractPhoneError } from '@/utils/phone-utils';
import { sessionExpired, setPhoneBindingEnforced } from '@/services/authSlice';
import type { PhoneErrorCode } from '@/types/PhoneErrorCode';

/**
 * The one refusal that must not end the session.
 * `PhoneBindingFilter` answers `403` for a user who has yet to bind a confirmed phone number,
 * and signing them out would tear down the session they need in order to bind one —
 * the code in the body is what tells the two kinds of `403` apart.
 */
const PHONE_BINDING_REQUIRED: PhoneErrorCode = 'PHONE_BINDING_REQUIRED';

export const unauthorizedMiddleware: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (isRejected(action)) {
    const payload = (action as PayloadAction<{ status: number }>)?.payload;
    const status: number = payload?.status;
    if (status === UNAUTHORIZED || status === FORBIDDEN) {
      if (extractPhoneError(payload)?.code === PHONE_BINDING_REQUIRED) {
        // Raising the barrier from here is what makes the refusal visible: the mode the client holds can be stale,
        // and this response is the only notice it gets.
        // Several refused queries repeat the dispatch,
        // which costs nothing — the flag is already set, so the state is unchanged and nothing re-renders.
        api.dispatch(setPhoneBindingEnforced(true));
      } else {
        // The server ended this session rather than the user,
        // so the sign-in form is where it belongs — it can send them back to the page the refusal interrupted.
        api.dispatch(sessionExpired());
      }
    }
  }
  return next(action);
};
